/**
 * ImageGenPanel — the honest 'Generate image' affordance for visual asset
 * types (IG caption etc.) in the Content Engine workspace.
 *
 * Contract:
 *   - The prompt is PREFILLED from the draft's content/brief but editable in
 *     a small textarea before generating.
 *   - Generation happens ONLY on explicit click (images cost money — never
 *     auto-generate on load).
 *   - Success renders the real bytes as <img src="data:...">, with
 *     'Download PNG' (client-side anchor) and 'Regenerate'.
 *   - Failure shows an honest error card with the engine's own reason
 *     (safety block, not-on-GCP, unconfigured). NEVER a placeholder image.
 *
 * All generation goes through the tested seam generateContentImage — this
 * component only wires state to it.
 */

import { useState } from 'react';
import { ImageIcon, Download, RefreshCw, Sparkles, AlertTriangle } from 'lucide-react';
import { C } from '../lib/tokens';
import type { ContentItemType } from '../lib/db/types';
import { buildImagePrompt } from '../lib/content/studioHome';
import { generateContentImage, type ContentImageResult } from '../lib/agent/contentEngine';

/** Aspect ratio per content type — matches the art direction in studioHome. */
const ASPECT_RATIO: Record<ContentItemType, string> = {
  'linkedin-post': '16:9',
  'x-thread': '16:9',
  'x-post': '16:9',
  blog: '16:9',
  email: '4:3',
  'ig-caption': '1:1',
};

export interface ImageGenPanelProps {
  type: ContentItemType;
  /** The draft's title/brief — seeds the editable prompt. */
  title: string;
  /** Optional draft body/brief blended into the prefilled prompt. */
  brief?: string;
}

type Phase =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'done'; result: ContentImageResult };

export function ImageGenPanel({ type, title, brief }: ImageGenPanelProps) {
  const [prompt, setPrompt] = useState(() => buildImagePrompt(type, brief?.trim() || title));
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' });

  const generate = async () => {
    setPhase({ kind: 'loading' });
    const result = await generateContentImage(prompt, ASPECT_RATIO[type]);
    setPhase({ kind: 'done', result });
  };

  const image =
    phase.kind === 'done' && 'imageBase64' in phase.result ? phase.result : null;
  const error =
    phase.kind === 'done' && 'error' in phase.result ? phase.result.error : null;

  const download = () => {
    if (!image) return;
    const a = document.createElement('a');
    a.href = `data:${image.mimeType};base64,${image.imageBase64}`;
    a.download = `${slug(title)}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <ImageIcon size={14} style={{ color: C.magenta, flexShrink: 0 }} />
        <span className="t-card-title" style={{ fontSize: 12, color: C.text }}>
          Generate image
        </span>
        <span className="t-meta" style={{ color: C.textDim }}>
          Imagen 3 · {ASPECT_RATIO[type]}
        </span>
      </div>

      <label className="t-meta" style={{ display: 'grid', gap: 4, color: C.textMuted }}>
        Prompt (edit before generating)
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={3}
          aria-label="Image prompt"
          placeholder="Describe the image to generate…"
          style={{
            width: '100%',
            boxSizing: 'border-box',
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 8,
            color: C.text,
            fontSize: 12,
            lineHeight: 1.5,
            padding: 8,
            resize: 'vertical',
          }}
        />
      </label>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <button
          onClick={generate}
          disabled={phase.kind === 'loading' || !prompt.trim()}
          className="glass-btn glass-btn--primary"
          style={{ padding: '5px 12px', fontSize: 12, opacity: prompt.trim() ? 1 : 0.5 }}
        >
          {phase.kind === 'loading' ? (
            <RefreshCw size={12} className="spin" />
          ) : image ? (
            <RefreshCw size={12} />
          ) : (
            <Sparkles size={12} />
          )}
          {phase.kind === 'loading'
            ? 'Generating…'
            : image
              ? 'Regenerate'
              : 'Generate image'}
        </button>
        {image && (
          <button
            onClick={download}
            className="glass-btn"
            style={{ padding: '5px 12px', fontSize: 12 }}
          >
            <Download size={12} />
            Download PNG
          </button>
        )}
      </div>

      {image && (
        <img
          src={`data:${image.mimeType};base64,${image.imageBase64}`}
          alt="Generated companion visual"
          style={{
            width: '100%',
            maxWidth: 420,
            borderRadius: 10,
            border: '1px solid rgba(255,255,255,0.12)',
            display: 'block',
          }}
        />
      )}

      {error && (
        <div
          role="alert"
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
            border: `1px solid ${C.orangeWarm}55`,
            background: `${C.orangeWarm}18`,
            borderRadius: 10,
            padding: 10,
          }}
        >
          <AlertTriangle size={14} style={{ color: C.orangeWarm, flexShrink: 0, marginTop: 1 }} />
          <div style={{ display: 'grid', gap: 2 }}>
            <span className="t-card-title" style={{ fontSize: 12, color: C.orangeWarm }}>
              Image not generated
            </span>
            <span className="t-meta" style={{ color: C.textMuted }}>
              {error}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

/** Filesystem-safe slug for the download filename. */
function slug(title: string): string {
  const s = title
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return s || 'argo-image';
}
